/* ==========================================================================
   Sousleau — shared storefront behaviour
   Depends on nothing but the browser and Shopify's own /cart AJAX API.
   Every cart mutation reads the authoritative server response before the
   interface claims success.
   ========================================================================== */

(function () {
  'use strict';

  const root = document.documentElement;

  /* ---------------------------------------------------------------- config */

  function config() {
    const el = document.getElementById('sousleau-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  const CFG = config();

  /* ------------------------------------------------------------- utilities */

  function routes() {
    return (window.routes ||= {});
  }

  /**
   * Cart endpoints. Dawn's layout publishes window.routes with the plain
   * paths ('/cart/add'); the AJAX API lives at the '.js' variants, which
   * validate stock and answer JSON (422 for a sold-out variant). The plain
   * endpoint answers with a redirect and must never be used from fetch.
   */
  function withJs(path) {
    return /\.js$/.test(path) ? path : path + '.js';
  }

  function cartRoute(suffix) {
    const r = routes();
    const base = r.cart_url || '/cart';
    switch (suffix) {
      case 'add':
        return withJs(r.cart_add_url || base + '/add');
      case 'change':
        return withJs(r.cart_change_url || base + '/change');
      case 'update':
        return withJs(r.cart_update_url || base + '/update');
      case 'base':
        return withJs(base);
      default:
        return base;
    }
  }

  /**
   * Format an amount given in the shop's minor currency units.
   * Uses Intl with the storefront locale so French shows "24,99 $".
   */
  function money(cents, whole = false) {
    const locale = CFG.locale || document.documentElement.lang || 'en';
    const currency = CFG.currency || 'CAD';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
        ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {})
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  function percent(value, digits) {
    const locale = CFG.locale || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(value);
    } catch (e) {
      return value.toFixed(digits);
    }
  }

  function t(key, replacements) {
    let str = (CFG.strings && CFG.strings[key]) || '';
    if (!str) return '';
    if (replacements) {
      Object.keys(replacements).forEach(function (k) {
        str = str.split('__' + k + '__').join(replacements[k]);
        str = str.split('{{ ' + k + ' }}').join(replacements[k]);
      });
    }
    return str;
  }

  function announce(message, level) {
    let region = document.getElementById('sl-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'sl-live-region';
      region.className = 'sl-visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', level === 'assertive' ? 'assertive' : 'polite');
      document.body.appendChild(region);
    }
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  /* ------------------------------------------------------------- cart core */

  const Cart = {
    _state: null,
    _listeners: [],

    subscribe(fn) {
      this._listeners.push(fn);
      if (this._state) fn(this._state);
      return () => {
        this._listeners = this._listeners.filter((f) => f !== fn);
      };
    },

    _publish(state) {
      this._state = state;
      this._listeners.forEach((fn) => {
        try {
          fn(state);
        } catch (e) {
          /* a broken listener must not break the cart */
        }
      });
      document.dispatchEvent(new CustomEvent('sousleau:cart:updated', { detail: state }));
    },

    async fetch() {
      const res = await fetch(cartRoute('base'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('cart-read-failed');
      const state = await this.sanitizeGifts(await res.json());
      this._publish(state);
      return state;
    },

    /**
     * Add one or more lines. Returns the authoritative cart afterwards.
     * Throws with `.userMessage` when Shopify rejected the request.
     */
    async add(items) {
      const body = { items: Array.isArray(items) ? items : [items] };
      const res = await fetch(cartRoute('add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error('cart-add-failed');
        err.userMessage = payload.description || payload.message || '';
        err.payload = payload;
        throw err;
      }
      const state = await this.fetch();
      return { added: payload, cart: state };
    },

    async change(line, quantity, properties) {
      const grouped = Array.isArray(line);
      const body = grouped ? { updates: Object.fromEntries(line.map((key, i) => [key, i === 0 ? quantity : 0])) } : typeof line === 'string' ? { id: line, quantity: quantity } : { line: line, quantity: quantity };
      if (properties) body.properties = properties;
      const res = await fetch(cartRoute(grouped ? 'update' : 'change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error('cart-change-failed');
        err.userMessage = payload.description || payload.message || '';
        throw err;
      }
      const state = await this.sanitizeGifts(payload);
      this._publish(state);
      return state;
    },

    /** Remove every line whose key is in `keys`, one call at a time. */
    async removeKeys(keys) {
      let state = null;
      for (const key of keys) {
        const res = await fetch(cartRoute('change'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          const err = new Error('cart-remove-failed');
          err.userMessage = payload.description || payload.message || '';
          throw err;
        }
        state = await res.json();
      }
      if (state) this._publish(state);
      return state;
    },

    isGiftItem(item) {
      return !!((item.properties && item.properties._sousleau_gift) ||
        (CFG.gift && CFG.gift.variants || []).some((v) => Number(v.id) > 0 && Number(v.id) === Number(item.variant_id)));
    },

    hasGiftConflict(state) {
      if (!state) return false;
      const ownTitles = CFG.gift && CFG.gift.discount_titles || [];
      const foreign = (d) => d.target_type !== 'shipping_line' && !ownTitles.includes(d.title);
      return (state.cart_level_discount_applications || []).some(foreign) ||
        (state.items || []).some((item) => !this.isGiftItem(item) &&
          ((item.line_level_discount_allocations || item.discount_allocations || []).some((d) => Number(d.amount) > 0 && foreign(d.discount_application || d)) || item.total_discount > 0));
    },

    async sanitizeGifts(state) {
      if (!CFG.gift || !CFG.gift.active) return state;
      const total = this.qualifyingTotal(state);
      let kept = false;
      const invalid = (state.items || []).filter((item) => {
        if (!this.isGiftItem(item)) return false;
        const rule = (CFG.gift.variants || []).find((v) => Number(v.id) === Number(item.variant_id));
        const valid = rule && total >= rule.threshold && (!rule.until || total < rule.until) &&
          item.quantity === 1 && item.final_line_price === 0 && !kept;
        if (valid) kept = true;
        return !valid;
      });
      if (!invalid.length) return state;
      const updated = await this.removeKeys(invalid.map((item) => item.key));
      if (updated) updated._gift_adjusted = true;
      return updated || state;
    },

    /** Keys of lines flagged as a campaign gift. */
    giftLineKeys(state) {
      const cart = state || this._state;
      if (!cart || !cart.items) return [];
      return cart.items
        .filter((item) => this.isGiftItem(item))
        .map((item) => item.key);
    },

    giftLine(state) {
      const cart = state || this._state;
      if (!cart || !cart.items) return null;
      return cart.items.find((item) => this.isGiftItem(item)) || null;
    },

    /**
     * Qualifying total = cart total minus gift lines minus excluded items.
     * Mirrors the rule described in the campaign configuration; it is a
     * presentation figure, never the amount charged.
     */
    qualifyingTotal(state) {
      const cart = state || this._state;
      if (!cart || !cart.items) return 0;
      const excludedTypes = ((CFG.gift && CFG.gift.excluded_product_types) || []).map((v) => v.trim()).filter(Boolean);
      return cart.items.reduce(function (sum, item) {
        if (item.properties && item.properties._sousleau_gift) return sum;
        if (item.product_type && excludedTypes.indexOf(item.product_type) !== -1) return sum;
        if (CFG.gift && CFG.gift.exclude_gift_cards && item.gift_card === true) return sum;
        return sum + item.final_line_price;
      }, 0);
    }
  };

  window.SousleauCart = Cart;
  window.SousleauMoney = money;
  window.SousleauT = t;

  // Custom elements also format prices in freshly rendered cart sections.
  if (!customElements.get('sl-money')) {
    customElements.define('sl-money', class extends HTMLElement {
      connectedCallback() {
        const cents = Number(this.dataset.cents);
        if (Number.isFinite(cents)) this.textContent = money(cents, this.hasAttribute('data-whole'));
      }
    });
  }

  /* -------------------------------------------------------- gift progress */

  /**
   * <sl-gift-progress> renders the tier bar. It works from the live cart, or
   * from a projected total when a product page passes `projection-cents`.
   * It renders nothing at all unless the campaign is marked active in the
   * theme configuration — a progress bar is not an offer.
   */
  class GiftProgress extends HTMLElement {
    connectedCallback() {
      const initialProjection = Number(this.getAttribute('data-projection-cents'));
      if (Number.isFinite(initialProjection) && initialProjection > 0) {
        this.projection = { cents: initialProjection };
      }
      this.tiers = this._readTiers();
      if (!this.tiers.length) {
        this.hidden = true;
        return;
      }
      this._unsubscribe = Cart.subscribe((state) => this.render(state));
      if (!Cart._state) {
        Cart.fetch().catch(() => {
          /* leave the last rendered state alone */
        });
      }
      this.addEventListener('sousleau:projection', (e) => {
        this.projection = e.detail;
        this.render(Cart._state);
      });
    }

    disconnectedCallback() {
      if (this._unsubscribe) this._unsubscribe();
    }

    _readTiers() {
      const script = this.querySelector('[data-gift-tiers]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    render(state) {
      if (!state) return;
      if (state._gift_adjusted && ['cart', 'gifts'].includes(this.getAttribute('data-context'))) {
        window.location.reload();
        return;
      }
      const active = this.getAttribute('data-active') === 'true';
      const conflict = Cart.hasGiftConflict(state);
      const current = Cart.qualifyingTotal(state);
      const projected = active && this.projection && this.projection.cents ? current + this.projection.cents : null;
      const reference = projected !== null ? projected : current;
      const max = this.tiers[this.tiers.length - 1].threshold;
      const pct = Math.max(0, Math.min(100, (reference / max) * 100));

      const fill = this.querySelector('[data-gift-fill]');
      if (fill) fill.style.width = pct + '%';

      this.querySelectorAll('[data-gift-marker]').forEach((marker) => {
        const threshold = parseInt(marker.getAttribute('data-gift-marker'), 10);
        marker.setAttribute('data-reached', reference >= threshold ? 'true' : 'false');
      });

      const currentEl = this.querySelector('[data-gift-current]');
      if (currentEl) currentEl.textContent = money(current);

      const projectedEl = this.querySelector('[data-gift-projected]');
      if (projectedEl) {
        if (projected !== null) {
          projectedEl.textContent = money(projected);
          projectedEl.closest('[data-gift-projected-wrap]')?.removeAttribute('hidden');
        } else {
          projectedEl.closest('[data-gift-projected-wrap]')?.setAttribute('hidden', '');
        }
      }

      const next = this.tiers.find((tier) => reference < tier.threshold);
      const message = this.querySelector('[data-gift-message]');
      const chooseLink = this.querySelector('[data-gift-choose]');
      const selected = Cart.giftLine(state);

      if (message) {
        if (!active) {
          message.textContent = t('gift_pending');
        } else if (conflict) {
          message.textContent = t('gift_non_combinable');
        } else if (selected && selected.final_line_price === 0) {
          message.textContent = t('gift_selected', { gift_name: selected.product_title });
        } else if (next) {
          const remaining = next.threshold - reference;
          message.textContent =
            projected !== null
              ? t('gift_gap_projected', { remaining_amount: money(remaining) })
              : t('gift_gap', { remaining_amount: money(remaining) });
        } else {
          message.textContent = t('gift_reached');
        }
      }

      if (chooseLink) {
        const unlocked = current >= this.tiers[0].threshold;
        chooseLink.hidden = false;
        chooseLink.textContent = active && unlocked && !conflict ? t('gift_choose') : t('gift_discover');
      }
    }
  }

  if (!customElements.get('sl-gift-progress')) {
    customElements.define('sl-gift-progress', GiftProgress);
  }

  /* --------------------------------------------- product add-to-cart form */

  /**
   * <sl-product-form> wraps a normal Shopify product form. Without JavaScript
   * the form still posts to /cart/add and the browser follows the redirect.
   */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.disabled = busy || this.button.dataset.variantAvailable === 'false';
      this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      const label = this.button.querySelector('[data-label]');
      const spinner = this.button.querySelector('[data-spinner]');
      if (label && spinner) {
        label.hidden = busy;
        spinner.hidden = !busy;
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || t('cart_add_error');
      this.errorEl.hidden = false;
    }

    clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy || this.button?.disabled) return; // guards duplicate or unavailable submissions
      this.busy = true;
      this.clearError();
      this.setBusy(true);

      /*
       * The form is converted to the `items` JSON format on purpose. Verified
       * on this store: the legacy `id` + `quantity` body (what a FormData post
       * sends) is accepted by /cart/add.js even for a sold-out, deny-policy
       * variant, and the customer only learns about it at checkout. The
       * `items` format answers 422 with the real message instead.
       */
      const formData = new FormData(this.form);
      const item = { id: parseInt(formData.get('id'), 10), quantity: parseInt(formData.get('quantity'), 10) || 1 };
      const properties = {};
      formData.forEach(function (value, key) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) item.properties = properties;
      if (formData.get('selling_plan')) item.selling_plan = formData.get('selling_plan');

      try {
        const res = await fetch(cartRoute('add'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [item] })
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.showError(payload.description || payload.message || '');
          return;
        }
        await Cart.fetch();
        announce(t('cart_updated'));
        this.dispatchEvent(new CustomEvent('sousleau:added', { bubbles: true, detail: payload }));
        document.dispatchEvent(new CustomEvent('sousleau:cart:added', { detail: payload }));
      } catch (e) {
        this.showError('');
      } finally {
        this.busy = false;
        this.setBusy(false);
      }
    }
  }

  if (!customElements.get('sl-product-form')) {
    customElements.define('sl-product-form', ProductForm);
  }

  /* ------------------------------------------------------ variant picker */

  /**
   * <sl-variant-picker> keeps price, availability, stock text, media and the
   * submitted variant id in step. Data comes from a JSON island rendered by
   * Liquid from the real variants.
   */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const script = this.querySelector('[data-variants]');
      try {
        this.variants = JSON.parse(script.textContent);
      } catch (e) {
        this.variants = [];
      }
      this._variantChange ||= () => this.onChange();
      this.addEventListener('change', this._variantChange);
      const scopeId = this.getAttribute('data-scope');
      this._scope = scopeId ? document.getElementById(scopeId) : null;
      this._quantityChange ||= () => this.updateProjection();
      this._scope?.addEventListener('sousleau:quantity', this._quantityChange);
      this.onChange(true);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._variantChange);
      this._scope?.removeEventListener('sousleau:quantity', this._quantityChange);
    }

    selectedVariant() {
      const checked = this.querySelector('input[name="sl-variant"]:checked');
      if (checked) {
        const id = parseInt(checked.value, 10);
        return this.variants.find((v) => v.id === id) || null;
      }
      const select = this.querySelector('select[data-variant-select]');
      if (select) {
        const id = parseInt(select.value, 10);
        return this.variants.find((v) => v.id === id) || null;
      }
      return this.variants[0] || null;
    }

    onChange(initial) {
      const variant = this.selectedVariant();
      if (!variant) return;
      const scopeId = this.getAttribute('data-scope');
      const scope = scopeId ? document.getElementById(scopeId) || document : document;

      const idInput = scope.querySelector('[data-variant-id]');
      if (idInput) idInput.value = variant.id;

      const priceEl = scope.querySelector('[data-variant-price]');
      if (priceEl) priceEl.textContent = money(variant.price);

      const compareEl = scope.querySelector('[data-variant-compare]');
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      const stockEl = scope.querySelector('[data-variant-stock]');
      if (stockEl) {
        stockEl.setAttribute('data-state', variant.stock_state);
        const text = stockEl.querySelector('[data-variant-stock-text]');
        if (text) text.textContent = variant.stock_label;
      }

      const unitEl = scope.querySelector('[data-variant-unit]');
      if (unitEl) {
        unitEl.textContent = variant.unit_price_label || '';
        unitEl.hidden = !variant.unit_price_label;
      }

      const submit = scope.querySelector('[data-add-button]');
      if (submit) {
        submit.dataset.variantAvailable = variant.available ? 'true' : 'false';
        submit.disabled = !variant.available || submit.getAttribute('aria-busy') === 'true';
        const label = submit.querySelector('[data-label]');
        if (label) {
          label.textContent = variant.available ? t('add_to_cart') : t('sold_out');
        }
      }

      const qty = scope.querySelector('[data-quantity-input]');
      if (qty) {
        const rule = variant.quantity_rule || {};
        qty.min = rule.min ?? 1;
        qty.step = rule.increment || 1;
        if (Number.isFinite(Number(rule.max)) && Number(rule.max) > 0) qty.max = rule.max;
        else qty.removeAttribute('max');
        qty.closest('sl-quantity')?.normalise?.();
      }

      if (!initial && variant.media_id) {
        const gallery = scope.querySelector('[data-variant-media]');
        if (gallery && variant.featured_image) {
          gallery.src = variant.featured_image;
          gallery.srcset = '';
          if (variant.featured_image_alt !== undefined) gallery.alt = variant.featured_image_alt || '';
        }
      }

      this.dispatchEvent(
        new CustomEvent('sousleau:variant:changed', { bubbles: true, detail: variant })
      );

      this.updateProjection();
    }

    updateProjection() {
      const scope = this._scope;
      const variant = this.selectedVariant();
      if (!scope || !variant) return;
      const qty = scope.querySelector('[data-quantity-input]');
      const projectionHost = scope.querySelector('sl-gift-progress');
      if (projectionHost) {
        const quantity = qty ? parseInt(qty.value, 10) || 1 : 1;
        projectionHost.dispatchEvent(
          new CustomEvent('sousleau:projection', { detail: { cents: variant.price * quantity } })
        );
      }
    }
  }

  if (!customElements.get('sl-variant-picker')) {
    customElements.define('sl-variant-picker', VariantPicker);
  }

  /* --------------------------------------------------- quantity stepper */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button[data-step]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const delta = parseInt(btn.getAttribute('data-step'), 10);
          this.step(delta);
        });
      });
      this.input.addEventListener('change', () => this.normalise());
      this.normalise();
    }

    normalise() {
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      const increment = parseInt(this.input.step, 10) || 1;
      let value = parseInt(this.input.value, 10);
      if (isNaN(value) || value < min) value = min;
      const steps = Math.max(0, Math.round((value - min) / increment));
      const maxSteps = max !== null ? Math.max(0, Math.floor((max - min) / increment)) : Infinity;
      value = min + Math.min(steps, maxSteps) * increment;
      this.input.value = value;
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.dispatchEvent(new CustomEvent('sousleau:quantity', { bubbles: true, detail: value }));
    }

    step(delta) {
      const increment = parseInt(this.input.step, 10) || 1;
      const value = (parseInt(this.input.value, 10) || 0) + delta * increment;
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  if (!customElements.get('sl-quantity')) {
    customElements.define('sl-quantity', QuantityInput);
  }

  /* --------------------------------------------- cart bubble / drawer sync */

  document.addEventListener('sousleau:cart:updated', function (e) {
    const cart = e.detail;
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.setAttribute('aria-label', t('cart_count', { count: cart.item_count }));
    });
    // Let Dawn's own cart components refresh from the server.
    document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items').forEach(function (el) {
      if (typeof el.onCartUpdate === 'function') el.onCartUpdate();
    });
  });

  /* ------------------------------------------------------ accordion polyfill
     `details` handles this natively; we only mirror aria for the TOC links. */

  document.addEventListener('click', function (event) {
    const link = event.target.closest?.('.sl-details__tabs a[href^="#"]');
    if (!link) return;
    const target = document.getElementById(link.hash.slice(1));
    if (!target || target.tagName !== 'DETAILS') return;
    target.open = true;
    target.querySelector('summary')?.focus({ preventScroll: true });
  });

  /* ------------------------------------------------------- table of contents */

  const tocLinks = document.querySelectorAll('[data-toc] a[href^="#"]');
  if (tocLinks.length && 'IntersectionObserver' in window) {
    const targets = [];
    tocLinks.forEach(function (link) {
      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (target) targets.push({ link: link, target: target });
    });
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          tocLinks.forEach((l) => l.removeAttribute('aria-current'));
          const match = targets.find((t) => t.target === entry.target);
          if (match) match.link.setAttribute('aria-current', 'true');
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    targets.forEach((t) => observer.observe(t.target));
  }

  /* ------------------------------------------------------------ misc guards */

  // Keep native focus visible for keyboard users even inside custom widgets.
  root.classList.add('sl-ready');
})();
